import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { getRouteApi, Link } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { BarLoading } from "@/components/Loading/BarLoading";
import { ResourceNotFoundError } from "@/features/error";
import { interlude } from "@/interlude";
import { FileUploadForm } from "@/pages/applications/pages/productFeeds/pages/detail/FileUploadForm";
import { UrlFeedForm } from "@/pages/applications/pages/productFeeds/pages/detail/UrlFeedForm";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const route = getRouteApi("/applications/product-feeds/$feedId");

const cssHeaderContainer = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const cssBackButton = css`
  text-decoration: none;
`;

export const FeedDetailPage: React.FC = function FeedDetailPage() {
  const { t } = useTranslation();
  const { feedId } = route.useParams();
  const feedQuery = interlude.productFeed.useGetFeed(
    {
      params: {
        feedId: Number(feedId),
      },
    },
    {
      refetchInterval: (query) => {
        // Poll every 5 seconds when feed is syncing
        return query.state.data?.status === "syncing" ? 5000 : false;
      },
    },
  );

  if (feedQuery.isPending) {
    return (
      <PageLayout>
        <BarLoading wrapperStyle={{ margin: "auto" }} />
      </PageLayout>
    );
  }

  // Throw ResourceNotFoundError for 404 to trigger 404 page
  if (feedQuery.isError) {
    const error = feedQuery.error;
    if (error instanceof AxiosError && error.response?.status === 404) {
      throw new ResourceNotFoundError(
        `Product feed with id ${feedId} not found`,
      );
    }
    throw error;
  }

  if (!feedQuery.data) {
    // This should not happen, but keep as safety check
    throw new ResourceNotFoundError(`Product feed with id ${feedId} not found`);
  }

  const feed = feedQuery.data;
  const isFileUpload = feed.sourceType === "file_upload";

  return (
    <PageLayout>
      <Grid container css={cssHeaderContainer}>
        <Grid>
          <PageTitle>{t("productFeed.page.title")}</PageTitle>
        </Grid>
        <Grid>
          <Link to="/applications/product-feeds" css={cssBackButton}>
            <Button variant="plain">{t("productFeed.backToOverview")}</Button>
          </Link>
        </Grid>
      </Grid>
      <Tabs value={0} aria-label="detail feed tabs">
        <Tab
          label={
            isFileUpload
              ? t("productFeed.page.fileUpload.tabTitle")
              : t("productFeed.page.urlFeed.tabTitle")
          }
        />
      </Tabs>
      <Box sx={{ pt: 1 }}>
        {isFileUpload ? (
          <FileUploadForm feed={feed} />
        ) : (
          <UrlFeedForm feed={feed} />
        )}
      </Box>
    </PageLayout>
  );
};
